import {
  Component, Input, OnInit, OnDestroy,
  ViewChild, ElementRef, AfterViewChecked,
  Output,
  EventEmitter
} from '@angular/core';
import {
  Firestore, collection, query, where, addDoc, collectionData, orderBy
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Observable, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';


type Readout = 'position' | 'observation' | 'time' | 'movement' | 'identity' | 'affect';

@Component({
  selector: 'app-being-chat',
  templateUrl: './being-chat.component.html',
  styleUrls: ['./being-chat.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})



export class BeingChatComponent implements OnInit, OnDestroy {



  @Input() being!: { id: string;[key: string]: any };
  @Input() isOpen!: boolean;

  @Output() closed = new EventEmitter<void>();

  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('messageInput') private messageInput!: ElementRef;

  creatingCategory = false
  beingId!: string;
  messages$!: Observable<any[]>;
  newMessage = '';
  categories: string[] = [];
  teachCategory = '';
  // Inline teaching — keyed to the gap message being answered
  teachingId: string | null = null;
  teachPrompt = '';
  teachReply = '';

  readouts: Array<{ key: Readout; label: string }> = [
    { key: 'position', label: 'where it is' },
    { key: 'observation', label: 'what it sees' },
    { key: 'movement', label: 'what it\'s doing' },
    { key: 'time', label: 'the time' },
    { key: 'identity', label: 'who it is' },
    { key: 'affect', label: 'how it feels' },
  ];

  loading = false;
  awaitingReply = false;
  unreadCount = 0;

  private subs: Subscription[] = [];
  private previousMessageCount = 0;

  constructor(private db: Firestore, private auth: Auth) { }

  ngOnInit() {
    if (!this.being) {
      console.warn('No beingId provided to BeingChatComponent');
      return;
    }

    this.beingId = this.being.id;

    // this.subs.push(
    //   (collectionData(collection(this.db, `beings/${this.beingId}/notions`)) as Observable<any[]>)
    //     .subscribe(list => {
    //       this.categories = [...new Set(
    //         (list ?? []).filter(n => n.role === 'utterance' && n.label).map(n => n.label)
    //       )].sort();
    //     })
    // );

    const messagesRef = collection(this.db, `conversations/${this.beingId}_owner/messages`);
    this.messages$ = collectionData(
      query(messagesRef, orderBy('timestamp', 'asc')),
      { idField: 'id' }
    );

    this.subs.push(this.messages$.subscribe(msgs => {
      this.categories = [...new Set(
        msgs.filter(m => m.kind === 'teach' && m.label).map(m => m.label as string)
      )].sort();

      if (!this.isOpen && msgs.length > this.previousMessageCount && this.previousMessageCount > 0) {
        this.unreadCount += msgs.length - this.previousMessageCount;
      }
      this.previousMessageCount = msgs.length;

      const last = msgs[msgs.length - 1];
      this.awaitingReply = !!last
        && last.senderId !== this.being?.id
        && (last.kind === 'text' || last.kind === 'teach');
      setTimeout(() => this.scrollToBottom());
    }));
  }


  closeChat() {
    this.closed.emit();
  }

  // ------------------------------------------------------------

  async sendMessage() {
    const text = this.newMessage.trim();
    if (!text) return;
    this.newMessage = '';
    await this.push({ kind: 'text', text });
  }

  /** Open the teach form under a gap. */
  openTeach(msg: any) {
    this.teachingId = msg.id;
    this.teachPrompt = msg.prompt ?? '';
    this.teachReply = '';
  }

  cancelTeach() {
    this.teachingId = null;
    this.teachPrompt = '';
    this.teachReply = '';
    this.creatingCategory = false;
    this.teachCategory = '';
  }

  async submitReply() {
    const reply = this.teachReply.trim();
    if (!reply || !this.teachPrompt) return;
    const prompt = this.teachPrompt;
    const label = this.teachCategory.trim() || null;
    this.cancelTeach();
    await this.push({ kind: 'teach', teach: { prompt, reply }, label });
  }

  /** Point at a sensor instead of supplying an answer. */
  async bindReadout(readout: Readout) {
    if (!this.teachPrompt) return;
    const prompt = this.teachPrompt;
    this.cancelTeach();
    await this.push({ kind: 'teach', teach: { prompt, readout }, label: this.teachCategory.trim() || null });
  }

  async gradeReply(msg: any, win: boolean) {
    if (!msg.notion || msg.source === 'readout') return;
    await this.push({ kind: 'grade', code: msg.notion, text: msg.text, win });
  }

  private async push(payload: Record<string, any>) {
    this.loading = true;
    try {
      await addDoc(
        collection(this.db, `conversations/${this.beingId}_owner/messages`),
        {
          senderId: this.auth.currentUser?.uid,
          senderName: 'You',
          timestamp: Date.now(),
          ...payload,
        },
      );
    } catch (err) {
      console.error('Failed to send:', err);
    } finally {
      this.loading = false;
    }
  }

  private scrollToBottom(force = false) {
    const el = this.messagesContainer?.nativeElement;
    if (!el) return;

    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (force || nearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }
}